import React, { useState, useRef, useEffect } from "react";
import { Bank } from "../domain/types/banks/Bank";
import { BankAccount } from "../domain/types/banks/BankAccount";
import Tooltip from "./common/Tooltip";
import { Trash2 } from "react-feather";
import { Button } from "flowbite-react";

interface Props {
  accountsByBank: { [bankId: string]: BankAccount[] };
  banks: { [bankId: string]: Bank };
  sharedAccounts: Set<string>;
  deletingAccountIds: Set<string>;
  onDeleteAccount?: (accountId: string) => void;
  isOwner?: boolean;
}

const BankAccountsList: React.FC<Props> = ({
  accountsByBank,
  banks,
  sharedAccounts,
  deletingAccountIds,
  onDeleteAccount,
  isOwner = false,
}) => {
  // State to keep track of accounts being removed
  const [removingAccountIds, setRemovingAccountIds] = useState<Set<string>>(
    new Set(),
  );

  // Ref to store timeout IDs
  const timeouts = useRef<NodeJS.Timeout[]>([]);

  const handleDeleteClick = (accountId: string) => {
    // Add account ID to the set of removing accounts
    setRemovingAccountIds((prev) => new Set(prev).add(accountId));

    // Delay the actual deletion to allow the animation to complete
    const timeoutId = setTimeout(() => {
      if (onDeleteAccount) {
        onDeleteAccount(accountId);
      }

      // Remove account ID from the set after deletion
      setRemovingAccountIds((prev) => {
        const updated = new Set(prev);
        updated.delete(accountId);
        return updated;
      });
    }, 300); // Duration should match the CSS transition duration

    // Store the timeout ID
    timeouts.current.push(timeoutId);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeouts.current.forEach((timeoutId) => clearTimeout(timeoutId));
      timeouts.current = [];
    };
  }, []);

  return (
    <div className="grid gap-6">
      {Object.keys(accountsByBank).map((bankId) => {
        const bank = banks[bankId];
        const accounts = accountsByBank[bankId];

        // Skip rendering if bank data is not available
        if (!bank) return null;

        return (
          <div key={bankId}>
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              {bank.logo && (
                <img src={bank.logo} alt={bank.name} className="h-6 w-6" />
              )}
              {bank.name}
            </h3>
            <div className="space-y-2">
              {accounts.map((account) => {
                const isShared = sharedAccounts.has(
                  `${account.bankId}-${account.mask}`,
                );
                const isRemoving = removingAccountIds.has(account.accountId);
                const isDeleting = deletingAccountIds.has(account.accountId);

                return (
                  <div
                    key={account.accountId}
                    className={`flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-md transition-opacity duration-300 ease-in-out ${
                      isRemoving ? "opacity-0" : "opacity-100"
                    }`}
                  >
                    <div>
                      <p className="font-semibold">
                        {account.name}
                        {isShared && (
                          <Tooltip content="Shared Account">
                            <span className="ml-2 text-sm text-gray-500">
                              (Shared)
                            </span>
                          </Tooltip>
                        )}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        ****{account.mask}
                      </p>
                    </div>
                    {isOwner && onDeleteAccount && (
                      <Button
                        size="xs"
                        color="red"
                        onClick={() => handleDeleteClick(account.accountId)}
                        disabled={isDeleting || isRemoving}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(BankAccountsList);
